import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface CostCode {
  id: string;
  code: string;
  name: string;
  division: string;
  isDefault: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectCostCode {
  id: string;
  projectId: string;
  costCodeId: string;
  budgetAmount?: number;
  costCode: CostCode;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCostCodeData {
  code: string;
  name: string;
  division: string;
}

export interface AssignCostCodesData {
  costCodeIds: string[];
}

export interface UpdateProjectCostCodeData {
  budgetAmount?: number;
}

interface CostCodesResponse {
  costCodes: CostCode[];
}

interface CostCodeResponse {
  costCode: CostCode;
}

interface ProjectCostCodesResponse {
  projectCostCodes: ProjectCostCode[];
}

interface ProjectCostCodeResponse {
  projectCostCode: ProjectCostCode;
}

interface SeedResponse {
  count: number;
  costCodes: CostCode[];
}

// Get all company cost codes
export function useCostCodes() {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["costCodes"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CostCodesResponse>("/api/cost-codes", {
        headers,
      });
    },
    enabled: auth.isSignedIn,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Create a custom cost code
export function useCreateCostCode() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCostCodeData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CostCodeResponse>("/api/cost-codes", {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costCodes"] });
    },
  });
}

// Seed default CSI cost codes
export function useSeedDefaultCodes() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<SeedResponse>("/api/cost-codes/seed", {
        method: "POST",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costCodes"] });
    },
  });
}

// Delete a cost code (only non-default codes)
export function useDeleteCostCode() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(`/api/cost-codes/${id}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costCodes"] });
    },
  });
}

// Update a cost code (only non-default codes)
export function useUpdateCostCode() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CreateCostCodeData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CostCodeResponse>(`/api/cost-codes/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costCodes"] });
    },
  });
}

// NOTE: useProjectCostCodes has been moved to useProjectCostCodes.ts
// Re-exported here for backward compatibility
export {
  useProjectCostCodes,
  useProjectCostCodeOptions,
  useFindProjectCostCode,
  type CostCodeSelectOption,
} from "./useProjectCostCodes";

// Assign cost codes to a project
export function useAssignCostCodes(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AssignCostCodesData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ProjectCostCodesResponse>(`/api/projects/${projectId}/cost-codes`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "costCodes"] });
    },
  });
}

// Remove a cost code from a project
export function useRemoveProjectCostCode(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectCostCodeId: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(
        `/api/projects/${projectId}/cost-codes/${projectCostCodeId}`,
        {
          method: "DELETE",
          headers,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "costCodes"] });
    },
  });
}

// Update project cost code budget
export function useUpdateProjectCostCode(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectCostCodeId,
      data,
    }: {
      projectCostCodeId: string;
      data: UpdateProjectCostCodeData;
    }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ProjectCostCodeResponse>(
        `/api/projects/${projectId}/cost-codes/${projectCostCodeId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "costCodes"] });
    },
  });
}
